import { ArchonIcon, CheckIcon, Cross1Icon, IconSize, Tooltip } from '@protoss/ui';
import * as React from 'react';
import './AiProvenanceBadge.css';

export interface AiProvenanceBadgeProps {
  onAccept: () => Promise<void>;
  onReject: () => Promise<void>;
}

export function AiProvenanceBadge({ onAccept, onReject }: AiProvenanceBadgeProps) {
  const [accepting, setAccepting] = React.useState(false);
  const [rejecting, setRejecting] = React.useState(false);

  const handleAccept = React.useCallback(async () => {
    if (accepting || rejecting) return;
    setAccepting(true);
    try {
      await onAccept();
    } finally {
      setAccepting(false);
    }
  }, [accepting, rejecting, onAccept]);

  const handleReject = React.useCallback(async () => {
    if (accepting || rejecting) return;
    setRejecting(true);
    try {
      await onReject();
    } finally {
      setRejecting(false);
    }
  }, [accepting, rejecting, onReject]);

  const label = 'AI suggested';

  return (
    <div className="ai-provenance-badge" role="status" aria-label={label}>
      <span className="ai-provenance-badge__icon" aria-hidden="true">
        <ArchonIcon size={IconSize.xs} variant="subtle" />
      </span>
      <span className="ai-provenance-badge__label">{label}</span>
      <span className="ai-provenance-badge__sep" aria-hidden="true" />
      <span className="ai-provenance-badge__actions">
        <Tooltip.Simple label="Accept AI suggestion">
          <button
            type="button"
            className="ai-provenance-badge__btn ai-provenance-badge__btn--accept"
            onClick={() => void handleAccept()}
            disabled={accepting || rejecting}
            aria-label="Accept AI suggestion"
          >
            {accepting ? (
              <span className="ai-provenance-badge__spinner" aria-hidden="true" />
            ) : (
              <CheckIcon width={IconSize.xs} height={IconSize.xs} aria-hidden="true" />
            )}
          </button>
        </Tooltip.Simple>
        <Tooltip.Simple label="Reject AI suggestion">
          <button
            type="button"
            className="ai-provenance-badge__btn ai-provenance-badge__btn--reject"
            onClick={() => void handleReject()}
            disabled={accepting || rejecting}
            aria-label="Reject AI suggestion"
          >
            {rejecting ? (
              <span className="ai-provenance-badge__spinner" aria-hidden="true" />
            ) : (
              <Cross1Icon width={IconSize.xs} height={IconSize.xs} aria-hidden="true" />
            )}
          </button>
        </Tooltip.Simple>
      </span>
    </div>
  );
}
